import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Activity, Clock, ChevronDown, ChevronUp, 
  Target, Zap, Users, TrendingUp, RefreshCw, ExternalLink,
  Shield, Award
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * FootballStatsPanel - Displays live football game statistics
 * Supports: StatMoose Football, PrestoSports XML
 * Shows: Score by quarter, team stats, stat leaders
 */
export const FootballStatsPanel = ({ 
  statsUrl, 
  isLive = true, 
  homeTeam, 
  awayTeam,
  compact = false 
}) => {
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [lastUpdate, setLastUpdate] = useState(null);

  // Extract StatMoose game ID from URL
  const extractGameId = useCallback((url) => {
    if (!url) return null;
    // Format: https://statmoose.com/football/{game-id}/stats
    const match = url.match(/\/football\/([a-f0-9-]+)/i);
    return match ? match[1] : null;
  }, []);

  // Fetch football stats
  const fetchStats = useCallback(async () => {
    if (!statsUrl) {
      setError("No stats URL provided");
      setLoading(false);
      return;
    }

    try {
      // Check if it's a StatMoose URL
      if (statsUrl.includes('statmoose.com')) {
        const gameId = extractGameId(statsUrl);
        if (!gameId) {
          setError("Invalid StatMoose football URL");
          setLoading(false);
          return;
        }

        // Try the backend proxy endpoint (avoids CORS/firewall blocks)
        const response = await fetch(`${API}/broadcasts/statmoose/proxy/${gameId}`);
        if (response.ok) {
          const data = await response.json();
          if (!data.error) {
            data._provider = 'statmoose';
            setGameData(data);
            setLastUpdate(new Date());
            setError(null);
          } else {
            setGameData({ _externalOnly: true, _provider: 'statmoose' });
          }
        } else {
          // Fall back to external link only
          setGameData({ _externalOnly: true, _provider: 'statmoose' });
        }
      } 
      // Check if it's a PrestoSports XML URL
      else if (statsUrl.includes('.xml') || statsUrl.includes('prestosports')) {
        // Use backend to parse XML
        const response = await fetch(`${API}/livestats/football/presto?url=${encodeURIComponent(statsUrl)}`);
        if (response.ok) {
          const data = await response.json();
          data._provider = 'presto';
          setGameData(data);
          setLastUpdate(new Date());
          setError(null);
        } else {
          setGameData({ _externalOnly: true, _provider: 'presto' });
        }
      } else {
        // Generic external link
        setGameData({ _externalOnly: true });
      }
    } catch (err) {
      console.error("Error fetching football stats:", err);
      setGameData({ _externalOnly: true });
    } finally {
      setLoading(false);
    }
  }, [statsUrl, extractGameId]);

  useEffect(() => {
    if (!statsUrl) return;
    
    fetchStats();
    
    // Auto-refresh every 10 seconds for live games
    if (isLive) {
      const interval = setInterval(fetchStats, 10000);
      return () => clearInterval(interval);
    }
  }, [statsUrl, isLive, fetchStats]);

  if (!statsUrl) return null;

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-white/10 p-4" data-testid="football-stats-loading">
        <div className="flex items-center justify-center gap-2 text-zinc-500">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading football stats...</span>
        </div>
      </div>
    );
  }

  // External link only mode
  if (gameData?._externalOnly) {
    return (
      <div className="bg-zinc-900/50 border border-white/10 p-4" data-testid="football-stats-external">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-500" />
            <span className="text-white font-semibold">FOOTBALL STATS</span>
          </div>
          <a 
            href={statsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-orange-500 hover:text-orange-400 text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            View Full Stats
          </a>
        </div>
      </div>
    );
  }

  if (error || !gameData) {
    return null;
  }

  // Render StatMoose football panel
  if (gameData._provider === 'statmoose') {
    return (
      <StatMooseFootballPanel 
        gameData={gameData} 
        statsUrl={statsUrl} 
        homeTeam={homeTeam} 
        awayTeam={awayTeam}
        expanded={expanded}
        setExpanded={setExpanded}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        lastUpdate={lastUpdate}
        compact={compact}
      />
    );
  }

  // Render PrestoSports football panel
  if (gameData._provider === 'presto') {
    return (
      <PrestoFootballPanel 
        gameData={gameData} 
        statsUrl={statsUrl}
        expanded={expanded}
        setExpanded={setExpanded}
        lastUpdate={lastUpdate}
        compact={compact}
      />
    );
  }

  return null;
};

// StatMoose Football Panel
const StatMooseFootballPanel = ({ 
  gameData, 
  statsUrl, 
  homeTeam, 
  awayTeam,
  expanded,
  setExpanded,
  activeTab,
  setActiveTab,
  lastUpdate,
  compact
}) => {
  const isComplete = gameData.status === 'final' || gameData.is_complete;
  
  // Get team data - handle StatMoose naming
  const getTeamData = (position) => {
    const prefix = position === 'home' ? 'home' : 'away';
    return {
      name: position === 'home' 
        ? (homeTeam?.abbreviation || homeTeam?.name || gameData[`${prefix}_team_name`] || 'HOME')
        : (awayTeam?.abbreviation || awayTeam?.name || gameData[`${prefix}_team_name`] || 'AWAY'),
      fullName: gameData[`${prefix}_team_name`],
      score: gameData[`${prefix}_score`] ?? 0,
      quarterScores: gameData.quarter_scores?.[prefix] || [],
      logo: gameData[`${prefix}_team_logo`]
    };
  };

  const homeData = getTeamData('home');
  const awayData = getTeamData('away');

  // Team stats from StatMoose
  const teamStats = gameData.team_stats || {};
  const homeStats = teamStats.home || {};
  const awayStats = teamStats.away || {};

  // Leaders data
  const leaders = gameData.leaders || {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/50 border border-white/10 overflow-hidden"
      data-testid="football-stats-panel"
    >
      {/* Header */}
      <div className="bg-black/50 px-4 py-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-500" />
            <span className="text-white font-semibold">FOOTBALL STATS</span>
            {!isComplete && (
              <span className="px-2 py-0.5 bg-red-600 text-white text-xs rounded animate-pulse">LIVE</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <a 
              href={statsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-orange-500 hover:text-orange-400 flex items-center gap-1"
            >
              Full Stats <ExternalLink className="w-3 h-3" />
            </a>
            {lastUpdate && (
              <span className="text-xs text-zinc-500">
                Updated {lastUpdate.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Score Display */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          {/* Away Team (Left) */}
          <div className="flex-1 text-center">
            {awayData.logo && (
              <img src={awayData.logo} alt={awayData.name} className="w-12 h-12 mx-auto mb-2 object-contain" />
            )}
            <div className="text-lg font-bold text-white">{awayData.name}</div>
            <div className="text-4xl font-bold text-white">{awayData.score}</div>
          </div>

          {/* Game Status */}
          <div className="px-4 text-center">
            <div className={`text-sm font-bold ${isComplete ? 'text-green-500' : 'text-red-500'}`}>
              {isComplete ? 'FINAL' : 'LIVE'}
            </div>
            {!isComplete && gameData.period && (
              <div className="text-xs text-zinc-400">
                {gameData.period} - {gameData.clock || ''}
              </div>
            )}
            {gameData.possession && (
              <div className="text-xs text-orange-500 mt-1">
                🏈 {gameData.possession}
              </div>
            )}
          </div>

          {/* Home Team (Right) */}
          <div className="flex-1 text-center">
            {homeData.logo && (
              <img src={homeData.logo} alt={homeData.name} className="w-12 h-12 mx-auto mb-2 object-contain" />
            )}
            <div className="text-lg font-bold text-white">{homeData.name}</div>
            <div className="text-4xl font-bold text-white">{homeData.score}</div>
          </div>
        </div>

        {/* Quarter Scores */}
        {(homeData.quarterScores.length > 0 || awayData.quarterScores.length > 0) && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-zinc-500">
                  <th className="text-left py-1 w-24">Team</th>
                  <th className="text-center py-1 w-10">1</th>
                  <th className="text-center py-1 w-10">2</th>
                  <th className="text-center py-1 w-10">3</th>
                  <th className="text-center py-1 w-10">4</th>
                  {(homeData.quarterScores.length > 4 || awayData.quarterScores.length > 4) && (
                    <th className="text-center py-1 w-10">OT</th>
                  )}
                  <th className="text-center py-1 w-12 font-bold">T</th>
                </tr>
              </thead>
              <tbody className="text-white">
                <tr>
                  <td className="py-1">{awayData.name}</td>
                  {[0,1,2,3].map(i => (
                    <td key={i} className="text-center py-1">{awayData.quarterScores[i] ?? '-'}</td>
                  ))}
                  {awayData.quarterScores.length > 4 && (
                    <td className="text-center py-1">{awayData.quarterScores[4]}</td>
                  )}
                  <td className="text-center py-1 font-bold">{awayData.score}</td>
                </tr>
                <tr>
                  <td className="py-1">{homeData.name}</td>
                  {[0,1,2,3].map(i => (
                    <td key={i} className="text-center py-1">{homeData.quarterScores[i] ?? '-'}</td>
                  ))}
                  {homeData.quarterScores.length > 4 && (
                    <td className="text-center py-1">{homeData.quarterScores[4]}</td>
                  )}
                  <td className="text-center py-1 font-bold">{homeData.score}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Expandable Section */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              {/* Tab Navigation */}
              <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
                {['summary', 'leaders', 'team'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-3 py-1 text-xs uppercase font-bold transition-colors ${
                      activeTab === tab 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-zinc-800 text-zinc-400 hover:text-white'
                    }`}
                  >
                    {tab === 'summary' ? 'Summary' : tab === 'leaders' ? 'Leaders' : 'Team Stats'}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="mt-4">
                {activeTab === 'summary' && (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <FootballStatRow label="First Downs" away={awayStats.first_downs} home={homeStats.first_downs} />
                    <FootballStatRow label="Total Yards" away={awayStats.total_yards} home={homeStats.total_yards} />
                    <FootballStatRow label="Rushing Yards" away={awayStats.rushing_yards} home={homeStats.rushing_yards} />
                    <FootballStatRow label="Passing Yards" away={awayStats.passing_yards} home={homeStats.passing_yards} />
                    <FootballStatRow label="Turnovers" away={awayStats.turnovers} home={homeStats.turnovers} isLowerBetter />
                    <FootballStatRow label="Time of Poss." away={awayStats.time_of_possession} home={homeStats.time_of_possession} isTime />
                  </div>
                )}

                {activeTab === 'leaders' && (
                  <div className="space-y-3">
                    {leaders.passing && (
                      <LeaderCategory 
                        title="Passing" 
                        awayLeader={leaders.passing.away} 
                        homeLeader={leaders.passing.home}
                        statLabel="YDS"
                      />
                    )}
                    {leaders.rushing && (
                      <LeaderCategory 
                        title="Rushing" 
                        awayLeader={leaders.rushing.away} 
                        homeLeader={leaders.rushing.home}
                        statLabel="YDS"
                      />
                    )}
                    {leaders.receiving && (
                      <LeaderCategory 
                        title="Receiving" 
                        awayLeader={leaders.receiving.away} 
                        homeLeader={leaders.receiving.home}
                        statLabel="YDS"
                      />
                    )}
                    {leaders.tackles && (
                      <LeaderCategory 
                        title="Tackles" 
                        awayLeader={leaders.tackles.away} 
                        homeLeader={leaders.tackles.home}
                        statLabel="TKL"
                      />
                    )}
                  </div>
                )}

                {activeTab === 'team' && (
                  <div className="space-y-2 text-sm">
                    <FootballStatRow label="3rd Down Conv." away={awayStats.third_down_conv} home={homeStats.third_down_conv} />
                    <FootballStatRow label="4th Down Conv." away={awayStats.fourth_down_conv} home={homeStats.fourth_down_conv} />
                    <FootballStatRow label="Penalties" away={awayStats.penalties} home={homeStats.penalties} isLowerBetter />
                    <FootballStatRow label="Sacks" away={awayStats.sacks} home={homeStats.sacks} />
                    <FootballStatRow label="Interceptions" away={awayStats.interceptions} home={homeStats.interceptions} />
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Expand/Collapse Button */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full mt-4 flex items-center justify-center gap-2 text-zinc-400 hover:text-white text-sm py-2"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {expanded ? 'Less Stats' : 'More Stats'}
        </button>
      </div>
    </motion.div>
  );
};

// PrestoSports Football Panel
const PrestoFootballPanel = ({ 
  gameData, 
  statsUrl,
  expanded,
  setExpanded,
  lastUpdate,
  compact
}) => {
  const score = gameData.score || {};
  const isComplete = score.is_final;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/50 border border-white/10 overflow-hidden"
      data-testid="football-stats-panel"
    >
      {/* Header */}
      <div className="bg-black/50 px-4 py-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-500" />
            <span className="text-white font-semibold">FOOTBALL STATS</span>
          </div>
          <a 
            href={statsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-orange-500 hover:text-orange-400 flex items-center gap-1"
          >
            Full Stats <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      {/* Score */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 text-center">
            <div className="text-lg font-bold text-white">{score.away_team}</div>
            <div className="text-4xl font-bold text-white">{score.away_score}</div>
          </div>
          <div className="px-4 text-center">
            <div className={`text-sm font-bold ${isComplete ? 'text-green-500' : 'text-red-500'}`}>
              {score.period || (isComplete ? 'FINAL' : 'LIVE')}
            </div>
          </div>
          <div className="flex-1 text-center">
            <div className="text-lg font-bold text-white">{score.home_team}</div>
            <div className="text-4xl font-bold text-white">{score.home_score}</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Football Stat Row Component
const FootballStatRow = ({ label, away, home, isLowerBetter = false, isTime = false }) => {
  const awayValue = isTime ? away : (parseFloat(away) || 0);
  const homeValue = isTime ? home : (parseFloat(home) || 0);
  
  let awayWinning = false;
  let homeWinning = false;
  
  if (!isTime) {
    awayWinning = isLowerBetter ? awayValue < homeValue : awayValue > homeValue;
    homeWinning = isLowerBetter ? homeValue < awayValue : homeValue > awayValue;
  }

  return (
    <div className="flex items-center justify-between col-span-1">
      <span className={`w-16 text-right ${awayWinning ? 'text-green-400 font-bold' : 'text-zinc-400'}`}>
        {away ?? '-'}
      </span>
      <span className="flex-1 text-center text-zinc-500 text-xs px-2">{label}</span>
      <span className={`w-16 text-left ${homeWinning ? 'text-green-400 font-bold' : 'text-zinc-400'}`}>
        {home ?? '-'}
      </span>
    </div>
  );
};

// Leader Category Component
const LeaderCategory = ({ title, awayLeader, homeLeader, statLabel }) => {
  return (
    <div className="bg-zinc-800/50 p-3 rounded">
      <div className="text-xs text-orange-500 font-bold uppercase mb-2">{title}</div>
      <div className="flex justify-between">
        {/* Away Leader */}
        <div className="text-left">
          {awayLeader ? (
            <>
              <div className="text-white font-medium">{awayLeader.name || awayLeader.player}</div>
              <div className="text-zinc-400 text-xs">
                {awayLeader.yards || awayLeader.value || awayLeader.stat} {statLabel}
              </div>
            </>
          ) : (
            <span className="text-zinc-500 text-xs">No stats</span>
          )}
        </div>
        {/* Home Leader */}
        <div className="text-right">
          {homeLeader ? (
            <>
              <div className="text-white font-medium">{homeLeader.name || homeLeader.player}</div>
              <div className="text-zinc-400 text-xs">
                {homeLeader.yards || homeLeader.value || homeLeader.stat} {statLabel}
              </div>
            </>
          ) : (
            <span className="text-zinc-500 text-xs">No stats</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default FootballStatsPanel;
